import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Box } from '@mui/material';

import { QRCodeTypeItem } from '~/components/organisms/SelectQrType/QRCodeTypeItem/QRCodeTypeItem';
import Accordion from '~/components/ui/Accordion/Accordion';
import Chip from '~/components/ui/Chip';
import { Text } from '~/components/ui/text';
import { DESKTOP_MIN_WIDTH, TABLET_MIN_WIDTH } from '~/constants/common';
import { QRCODE_STATIC_NAME_PATH } from '~/constants/qr-code';
import { STATIC_TYPE_LIST } from '~/constants/qrTypes';
import { dynamicQRsIcons } from '~/pages/CreateQR/utils';
import useBuilderStore from '~/store/useBuilderStore';
import useSidebarStore from '~/store/useSidebarStore';
import { COLORS } from '~/styles/colors';

import { StyledAlert, StyledAlertGotIt, StyledItem } from './styled';

type SelectQrTypeProps = {
  onStaticQrTypeClick: (id: string) => void;
  onDynamicQrTypeClick: (index: number) => void;
  isShowAlert: boolean;
  isStaticDisabled?: boolean;
  isDynamicDisabled?: boolean;
};

export const SelectQrType = ({
  isShowAlert: isShowAlertDefault = false,
  onDynamicQrTypeClick,
  onStaticQrTypeClick,
  isStaticDisabled,
  isDynamicDisabled,
}: SelectQrTypeProps) => {
  const { t } = useTranslation();
  const { setSelectQrTypeHovered } = useBuilderStore();
  const styles = useStyles();

  const { isDesktopSidebarOpen } = useSidebarStore();

  const [isShowAlert, setIsShowAlert] = useState(isShowAlertDefault);

  useEffect(() => {
    setIsShowAlert(isShowAlertDefault);
  }, [isShowAlertDefault]);

  return (
    <Box className="templates">
      <Box sx={styles.container}>
        <Box sx={styles.headerRow}>
          <Text variant="h2" sx={styles.headerText}>
            {t('createQrStep1:h1')}
          </Text>
          <Chip type="success" label={t('createQrStep1:label1')} />
        </Box>
        <Text variant="t16Regular" sx={styles.subHeaderText}>
          {t('createQrStep1:h2')}
        </Text>

        {isShowAlert && (
          <StyledAlert
            variant="filled"
            severity="warning"
            icon={<>🚨</>}
            action={
              <StyledAlertGotIt aria-label="close" color="inherit" size="small" onClick={() => setIsShowAlert(false)}>
                {t('createQrStep1:alertClose')}
              </StyledAlertGotIt>
            }
          >
            <Text variant="t16Medium">{t('alerts:editType')}</Text>
          </StyledAlert>
        )}

        <Box sx={styles.dynamicGrid} className={isDesktopSidebarOpen ? 'sidebarOpened' : undefined}>
          {dynamicQRsIcons.map(({ Icon, id }, index) => (
            <Box
              onMouseEnter={() => setSelectQrTypeHovered(id)}
              onMouseLeave={() => setSelectQrTypeHovered('')}
              key={id}
            >
              <StyledItem
                $isDynamicDisabled={isDynamicDisabled}
                disabled={isDynamicDisabled}
                onClick={() => !isDynamicDisabled && onDynamicQrTypeClick(index)}
              >
                <Box sx={styles.dynamicIconWrapper}>
                  <Icon
                    style={{
                      color: isDynamicDisabled ? COLORS.gray04 : COLORS.blue,
                      width: '44px',
                      height: '44px',
                    }}
                  />
                </Box>
                <div>
                  <Text variant="t16Medium" sx={styles.dynamicItemText}>
                    {t(`createQrStep1:items:b${index + 1}:name`)}
                  </Text>
                  <Text variant="inputVerySmall" sx={styles.dynamicItemSubText}>
                    {t(`createQrStep1:items:b${index + 1}:subName`)}
                  </Text>
                </div>
              </StyledItem>
            </Box>
          ))}
        </Box>
      </Box>

      <Accordion
        variant="noBg"
        summary={
          <div>
            <Box sx={styles.staticAccordionSummary}>
              <Text variant="h2">{t('createQrStep1:h3')}</Text>
              <Chip label={t('createQrStep1:label2')} type="disabled" />
            </Box>
            <Text variant="t16Regular" sx={styles.staticAccordionDescription}>
              {t('createQrStep1:staticDescription')}
            </Text>
          </div>
        }
        details={
          <Box sx={styles.dynamicGrid} className={isDesktopSidebarOpen ? 'sidebarOpened' : undefined}>
            {STATIC_TYPE_LIST.map(({ id, Icon }, index) => (
              <QRCodeTypeItem
                key={index}
                Icon={Icon}
                title={t(QRCODE_STATIC_NAME_PATH[id])}
                description={t(`createQrStep1:items:c${index + 1}:subName`)}
                onClick={() => !isStaticDisabled && onStaticQrTypeClick(id)}
                disabled={isStaticDisabled || index > 6}
              />
            ))}
          </Box>
        }
      />
    </Box>
  );
};

export const useStyles = () => {
  return {
    container: css`
      margin-top: 8px;
      margin-bottom: 48px;
    `,
    headerRow: css`
      display: flex;
      align-items: center;
      gap: 12px;
    `,
    headerText: css`
      color: ${COLORS.textMain};
    `,
    subHeaderText: css`
      color: ${COLORS.grey400};
      margin-top: 8px;
    `,
    dynamicGrid: css`
      margin-top: 20px;
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      gap: 12px;

      @media (min-width: 360px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (min-width: ${TABLET_MIN_WIDTH}px) {
        grid-template-columns: repeat(3, 1fr);
      }

      &:not(.sidebarOpened) {
        @media (min-width: ${DESKTOP_MIN_WIDTH}px) {
          max-width: calc(55dvw);
        }

        @media (min-width: 1200px) {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      &.sidebarOpened {
        @media (min-width: ${DESKTOP_MIN_WIDTH}px) {
          max-width: 50dvw;
          grid-template-columns: repeat(2, 1fr);
        }

        @media (min-width: 1140px) {
          grid-template-columns: repeat(3, 1fr);
        }

        @media (min-width: 1300px) {
          grid-template-columns: repeat(4, 1fr);
        }
      }
    `,
    dynamicIconWrapper: css`
      padding: 11px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      background-color: ${COLORS.blue50};
    `,
    dynamicItemText: css`
      color: ${COLORS.textMain};
      text-align: center;
    `,
    dynamicItemSubText: css`
      color: ${COLORS.grey400};
      text-align: center;
    `,
    staticAccordionSummary: css`
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;
    `,
    staticAccordionDescription: css`
      color: ${COLORS.grey400};
    `,
    staticGrid: css`
      margin-top: 20px;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 12px;
    `,
  };
};
